import React, { useEffect, useState } from "react";
import { Collection } from "../../Model/Collection";
import { ETemperatureSensorType } from "../../Model/ETemperatureSensorType";
import { TemperatureSensor } from "../../Model/Temperature";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import { getModuleTemperatureChanged } from "../../redux/reducers/TemperatureReducer";
import SwitchButton from "../Buttons/SwitchButton";
import TemperatureApi from "./../../api/TemperatureApi";
import "./temperature.css";
import TemperatureCard from "./TemperatureCard";
import { EGraphType, TemperatureSetting } from "./TemperatureChartUtil";
import TemperatureGraphColor from "./TemperatureGraphColor";

type Props = {
  moduleIdentifier: string;
  temperatureSensorCollection: Collection<TemperatureSensor>;
  sensorsVisibility: { [index: string]: boolean };
  temperatureSetting: TemperatureSetting;
  setSensorsVisibility: (sensorsVisibility: {
    [index: string]: boolean;
  }) => void;
  setTemperatureSetting: (temperatureSetting: TemperatureSetting) => void;
  setAverageSensorValue: (value: number) => void;
  setTargetSensorValue: (value: number) => void;
  style?: React.CSSProperties;
};

const initialState: { [index: string]: number } = {};

function isAllSensorVisible(
  temperatureSensorCollection: Collection<TemperatureSensor>,
  sensorsVisibility: { [index: string]: boolean },
): boolean {
  // get excluded sensor name
  let excludedSensorName = "";
  for (let i = 0; i < temperatureSensorCollection.allIds.length; i++) {
    const sensorName = temperatureSensorCollection.allIds[i];
    if (
      temperatureSensorCollection.byId[sensorName].type ===
      ETemperatureSensorType.Feedback
    ) {
      excludedSensorName = sensorName;
      break;
    }
  }
  // get only included sensor visibility
  const { [excludedSensorName]: _, ...includedSensorsVisibility } =
    sensorsVisibility;

  return Object.values(includedSensorsVisibility).every((value) => value);
}

export default function TemperatureSensors({
  moduleIdentifier,
  temperatureSensorCollection,
  sensorsVisibility,
  temperatureSetting,
  setSensorsVisibility,
  setTemperatureSetting,
  setAverageSensorValue,
  setTargetSensorValue,
  style,
}: Props) {
  const [sensorValues, setSensorValues] = useState(initialState);
  const temperatureReadings = useTypedSelector((state) =>
    getModuleTemperatureChanged(state, moduleIdentifier),
  );

  //-------OnToogleAllSensor Handler-------//
  const setAllSensorVisibility = (visibility: boolean) => {
    let newSensorVisibility: { [index: string]: boolean } = {};
    for (let i = 0; i < temperatureSensorCollection.allIds.length; i++) {
      const sensorName = temperatureSensorCollection.allIds[i];
      newSensorVisibility[sensorName] = visibility;
    }
    setSensorsVisibility(newSensorVisibility);
    setTemperatureSetting({
      ...temperatureSetting,
      showGraph: visibility === true ? EGraphType.AllGraph : EGraphType.Custom,
    });
  };

  //-------OnClick Handler-------//
  const OnTemperatureCardClicked = (
    sensorName: string,
    visibility: boolean,
  ) => {
    const newSensorVisibility = {
      ...sensorsVisibility,
      [sensorName]: visibility,
    };
    setSensorsVisibility(newSensorVisibility);
    setTemperatureSetting({
      ...temperatureSetting,
      showGraph: isAllSensorVisible(
        temperatureSensorCollection,
        newSensorVisibility,
      )
        ? EGraphType.AllGraph
        : EGraphType.Custom,
    });
  };

  //-------get current temperature reading value-------//
  useEffect(() => {
    TemperatureApi.getTemperatureReading(moduleIdentifier).then(
      (temperatureReading) => {
        let sensorValues: { [index: string]: number } = {};
        for (let i = 0; i < temperatureReading.length; i++) {
          sensorValues[temperatureReading[i].sensorName] =
            temperatureReading[i].value;
        }
        setSensorValues(sensorValues);
        setAverageSensorValue(sensorValues["Average"]);
        setTargetSensorValue(sensorValues["Target Temperature"]);
      },
    );
  }, [moduleIdentifier, setAverageSensorValue]);

  //-------Update temperature reading value-------//
  useEffect(() => {
    if (temperatureReadings) {
      setSensorValues(temperatureReadings.data);
      setAverageSensorValue(temperatureReadings.data["Average"]);
      setTargetSensorValue(temperatureReadings.data["Target Temperature"]);
    }
  }, [temperatureReadings, setAverageSensorValue]);

  console.log("DEBUG", "TEMPSENSORS", { sensorsVisibility });

  const getSensorValue = (
    sensorName: string,
    sensorType: ETemperatureSensorType,
  ) => {
    if (sensorValues[sensorName] == null) {
      return "...";
    }
    if (sensorType === ETemperatureSensorType.Output) {
      return `${sensorValues[sensorName].toFixed(1)}%`;
    } else if (sensorType === ETemperatureSensorType.Fan) {
      return `${sensorValues[sensorName].toFixed(1)} RPS`;
    } else {
      return `${sensorValues[sensorName].toFixed(1)}\u2103`;
    }
  };

  let cards = [];

  for (const sensorName of temperatureSensorCollection.allIds) {
    const sensor = temperatureSensorCollection.byId[sensorName];
    const sensorVisibility =
      sensorsVisibility[sensor.name] == null
        ? true
        : sensorsVisibility[sensor.name];
    const sensorValue = getSensorValue(sensor.name, sensor.type);

    if (sensor.type !== ETemperatureSensorType.Feedback) {
      cards.push(
        <TemperatureCard
          key={sensor.name}
          color={TemperatureGraphColor.getTemperatureGraphColor(sensor.name)}
          title={sensor.name}
          isOn={sensorVisibility}
          onClick={() =>
            OnTemperatureCardClicked(sensor.name, !sensorVisibility)
          }
          value={sensorValue}
        />,
      );
    }
  }

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        ...style,
      }}
    >
      <div
        style={{
          backgroundColor: "#414042",
          display: "flex",
          justifyContent: "flex-end",
          color: "#FFFFFF",
          padding: 19,
          alignItems: "center",
          height: "90px",
        }}
      >
        <div
          style={{
            marginRight: 13,
          }}
        >
          Show All Temperature
        </div>
        <SwitchButton
          isOn={isAllSensorVisible(
            temperatureSensorCollection,
            sensorsVisibility,
          )}
          onToggle={(value: boolean) => setAllSensorVisibility(value)}
        />
      </div>
      {cards}
    </div>
  );
}
